/**
 * Client brand color -> derived palette (V16 "client brand color" —
 * Stream A). One stored brand color on the deck theme drives a full derived
 * OKLCH palette for the `momentum_v3` template family, including the dark
 * surfaces (see web/src/lib/theme.ts themeStyle()).
 *
 * Pure color math, no dependencies (not even a sibling domain file), so it
 * can be imported as a real runtime value from the web bundle via the
 * `@shared/*` alias (-> src/domain) with zero risk of dragging in anything
 * Node-only.
 *
 * sRGB <-> linear sRGB <-> OKLab <-> OKLCH conversions follow Björn
 * Ottosson's OKLab reference (https://bottosson.github.io/posts/oklab/).
 */

/** A color in OKLCH space: lightness [0,1], chroma [0, ~0.4], hue in degrees [0,360). */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

/**
 * The full palette derived from one brand hex color, all lowercase
 * `#rrggbb`. See deriveBrandPalette for the derivation of each slot.
 */
export interface BrandPalette {
  primary: string;
  primaryDeep: string;
  secondary: string;
  accent: string;
  darkBg: string;
  darkBg2: string;
  darkText2: string;
  darkText3: string;
}

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#rgb` -> `#rrggbb`, lowercased. Returns null for anything else. */
function normalizeHex(hex: string): string | null {
  if (typeof hex !== 'string' || !HEX_RE.test(hex)) return null;
  const body = hex.slice(1).toLowerCase();
  if (body.length === 3) {
    return `#${body[0]}${body[0]}${body[1]}${body[1]}${body[2]}${body[2]}`;
  }
  return `#${body}`;
}

function hexToRgb01(hex: string): { r: number; g: number; b: number } {
  const normalized = normalizeHex(hex);
  if (!normalized) throw new Error(`brand-palette: invalid hex color "${hex}"`);
  const int = parseInt(normalized.slice(1), 16);
  return {
    r: ((int >> 16) & 0xff) / 255,
    g: ((int >> 8) & 0xff) / 255,
    b: (int & 0xff) / 255,
  };
}

/** `r`/`g`/`b` are post-gamma sRGB values, nominally [0,1]; out-of-range is clamped. */
function rgb01ToHex(r: number, g: number, b: number): string {
  const toByte = (c: number) => Math.round(Math.min(1, Math.max(0, c)) * 255);
  const toHexByte = (n: number) => n.toString(16).padStart(2, '0');
  return `#${toHexByte(toByte(r))}${toHexByte(toByte(g))}${toHexByte(toByte(b))}`;
}

function srgbChannelToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

/** `c` is clamped to [0,1] first, so this never receives a negative base for `Math.pow`. */
function linearChannelToSrgb(c: number): number {
  const clamped = Math.min(1, Math.max(0, c));
  return clamped <= 0.0031308 ? clamped * 12.92 : 1.055 * Math.pow(clamped, 1 / 2.4) - 0.055;
}

interface LinearRgb {
  r: number;
  g: number;
  b: number;
}

interface Lab {
  L: number;
  a: number;
  b: number;
}

function linearSrgbToOklab({ r, g, b }: LinearRgb): Lab {
  const l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
  const m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
  const s = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;

  const l_ = Math.cbrt(l);
  const m_ = Math.cbrt(m);
  const s_ = Math.cbrt(s);

  return {
    L: 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_,
    a: 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_,
    b: 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_,
  };
}

function oklabToLinearSrgb({ L, a, b }: Lab): LinearRgb {
  const l_ = L + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = L - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = L - 0.0894841775 * a - 1.291485548 * b;

  const l = l_ * l_ * l_;
  const m = m_ * m_ * m_;
  const s = s_ * s_ * s_;

  return {
    r: 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
    g: -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
    b: -0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s,
  };
}

function labToOklch(lab: Lab): Oklch {
  const c = Math.sqrt(lab.a * lab.a + lab.b * lab.b);
  let h = (Math.atan2(lab.b, lab.a) * 180) / Math.PI;
  if (h < 0) h += 360;
  return { l: lab.L, c, h };
}

function oklchToLab(oklch: Oklch): Lab {
  const hRad = (oklch.h * Math.PI) / 180;
  return { L: oklch.l, a: oklch.c * Math.cos(hRad), b: oklch.c * Math.sin(hRad) };
}

/** Converts a hex color (`#rgb` or `#rrggbb`, case-insensitive) to OKLCH. */
export function hexToOklch(hex: string): Oklch {
  const rgb = hexToRgb01(hex);
  const linear: LinearRgb = {
    r: srgbChannelToLinear(rgb.r),
    g: srgbChannelToLinear(rgb.g),
    b: srgbChannelToLinear(rgb.b),
  };
  return labToOklch(linearSrgbToOklab(linear));
}

function isInGamut(rgb: LinearRgb, eps = 1e-4): boolean {
  return (
    rgb.r >= -eps &&
    rgb.r <= 1 + eps &&
    rgb.g >= -eps &&
    rgb.g <= 1 + eps &&
    rgb.b >= -eps &&
    rgb.b <= 1 + eps
  );
}

/**
 * Reduces chroma (holding L and H fixed) by bisection until the sRGB result
 * is in gamut. L is clamped to [0,1] first — at chroma 0 that is always in
 * gamut (a=b=0 collapses every OKLab channel to L, so linear r=g=b=L^3), so
 * the bisection's lower bound is always valid.
 */
function clipToGamut(oklch: Oklch): Oklch {
  const l = Math.min(1, Math.max(0, oklch.l));
  if (oklch.c <= 0) return { l, c: 0, h: oklch.h };

  if (isInGamut(oklabToLinearSrgb(oklchToLab({ l, c: oklch.c, h: oklch.h })))) {
    return { l, c: oklch.c, h: oklch.h };
  }

  let lo = 0;
  let hi = oklch.c;
  for (let i = 0; i < 32; i++) {
    const mid = (lo + hi) / 2;
    if (isInGamut(oklabToLinearSrgb(oklchToLab({ l, c: mid, h: oklch.h })))) {
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return { l, c: lo, h: oklch.h };
}

/**
 * Converts OKLCH to a lowercase `#rrggbb` hex color, clipping chroma down
 * (holding L and H fixed) until the sRGB result is in gamut. Never throws —
 * any {l,c,h}, in or out of the representable range, yields a valid hex.
 */
export function oklchToHex(oklch: Oklch): string {
  const clipped = clipToGamut(oklch);
  const linear = oklabToLinearSrgb(oklchToLab(clipped));
  return rgb01ToHex(
    linearChannelToSrgb(linear.r),
    linearChannelToSrgb(linear.g),
    linearChannelToSrgb(linear.b),
  );
}

/**
 * A brand color counts as "usable" for deriving a palette when it is a
 * valid hex AND has enough chroma to read as a color (not a black/white/
 * gray/near-neutral) AND isn't so dark or so light that derived surfaces
 * would be unreadable.
 */
export function isUsableBrandColor(hex: string): boolean {
  let oklch: Oklch;
  try {
    oklch = hexToOklch(hex);
  } catch {
    return false;
  }
  return oklch.c >= 0.06 && oklch.l > 0.2 && oklch.l < 0.95;
}

/**
 * V16 "client brand color" (Stream C — wiring): the minimum extraction
 * confidence (src/server/logo-color.ts ExtractedColor.confidence, the
 * winning bucket's share of counted pixels/occurrences) below which a
 * logo-extracted color is too uncertain to drive the deck palette, even if
 * the hex itself would otherwise pass isUsableBrandColor.
 */
export const MIN_BRAND_COLOR_CONFIDENCE = 0.35;

/**
 * The hex to actually apply from an extracted logo color — only when its
 * confidence clears MIN_BRAND_COLOR_CONFIDENCE AND the hex itself passes
 * isUsableBrandColor (has enough chroma, isn't too dark/light). undefined
 * for a null/absent color, a too-uncertain extraction, or an unusable hex —
 * the caller then leaves the deck on its family palette instead.
 */
export function usableBrandColorHex(
  color: { hex: string; confidence: number } | null | undefined,
): string | undefined {
  if (!color) return undefined;
  if (color.confidence < MIN_BRAND_COLOR_CONFIDENCE) return undefined;
  return isUsableBrandColor(color.hex) ? color.hex : undefined;
}

/**
 * Derives the full momentum_v3 palette from one brand hex color. Every slot
 * rides the brand's own hue; lightness and chroma are fixed (or capped by
 * the brand's own chroma) per slot so the derived palette stays inside a
 * readable, print-safe range regardless of how saturated the source color
 * is. darkBg/darkBg2 are tinted near-blacks (chroma 0.02) chosen to keep
 * white text above 12:1 contrast — see test/domain/brand-palette.test.ts.
 */
export function deriveBrandPalette(hex: string): BrandPalette {
  const brand = hexToOklch(hex);
  const h = brand.h;
  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

  return {
    // Tags, numbers, donut slice, rules.
    primary: oklchToHex({ l: clamp(brand.l, 0.45, 0.78), c: Math.min(brand.c, 0.2), h }),
    // Text/icons on light surfaces; replaces the CSS "green-deep" mix.
    primaryDeep: oklchToHex({ l: 0.36, c: Math.min(brand.c, 0.12), h }),
    // Tints, stage bars on light.
    secondary: oklchToHex({ l: 0.94, c: 0.035, h }),
    // Dark-surface emphasis.
    accent: oklchToHex({ l: 0.82, c: Math.min(brand.c * 1.1, 0.16), h }),
    // Tinted near-black surfaces.
    darkBg: oklchToHex({ l: 0.19, c: 0.02, h }),
    darkBg2: oklchToHex({ l: 0.24, c: 0.02, h }),
    darkText2: oklchToHex({ l: 0.78, c: 0.01, h }),
    darkText3: oklchToHex({ l: 0.6, c: 0.01, h }),
  };
}
